/**
 * File validation utilities for survey sections
 */

export interface FileValidationResult {
  readonly isValid: boolean;
  readonly errorMessage?: string;
}

export interface FileValidationOptions {
  readonly allowedTypes?: string[]; // e.g., ['application/pdf', 'image/jpeg']
  readonly maxSize?: number; // in bytes
  readonly checkEncryption?: boolean; // Only for PDFs
}

const checkForValidDocument = async (file: Blob): Promise<boolean> => {
  try {
    const text = await file.text();
    if (
      text.includes("Encrypt") ||
      text
        .substring(text.lastIndexOf("<<"), text.lastIndexOf(">>"))
        .includes("/Encrypt")
    ) {
      return true;
    }
    return false;
  } catch {
    return true;
  }
};

/**
 * Validates files based on provided options
 */
export const validateFiles = async (
  file: File,
  options: FileValidationOptions = {},
): Promise<FileValidationResult> => {
  const {
    allowedTypes = ["application/pdf"],
    maxSize,
    checkEncryption = true,
  } = options;

  let errorMessage: string | undefined;

  let isValid = true;
  let currentError: string | undefined;

  // Check file type
  if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
    isValid = false;

    // Determine error message based on file type
    if (file.type.startsWith("image/")) {
      currentError = "Images are not allowed";
    } else {
      currentError = `File type ${file.type} is not allowed`;
    }
  }

  // Check file size
  if (isValid && maxSize && file.size > maxSize) {
    isValid = false;
    currentError = `File size exceeds maximum allowed size of ${maxSize} bytes`;
  }

  // Check PDF encryption
  if (isValid && checkEncryption && file.type === "application/pdf") {
    const encrypted = await checkForValidDocument(file);
    if (encrypted) {
      isValid = false;
      currentError =
        "The file you are attempting to upload is either encrypted or password protected. Please try sourcing a different version without password protection, or contact the owner of this file";
    }
  }

  if (!isValid) {
    // Use the first error message encountered
    if (!errorMessage && currentError) {
      errorMessage = currentError;
    }
  }

  return {
    isValid,
    errorMessage,
  };
};

/**
 * Checks if a PDF file contains active content such as JavaScript or embedded files.
 */
export const checkForActiveContent = async (file: Blob): Promise<boolean> => {
  try {
    const buffer = await file.arrayBuffer();
    const text = new TextDecoder("iso-8859-1").decode(buffer);
    return (
      text.includes("/JavaScript") ||
      text.includes("/JS ") ||
      text.includes("/EmbeddedFile")
    );
  } catch {
    return false;
  }
};

/**
 * Validates PDF files specifically (convenience wrapper)
 */
export const validatePdfFiles = async (
  file: File,
): Promise<FileValidationResult> => {
  return validateFiles(file, {
    allowedTypes: ["application/pdf"],
    checkEncryption: true,
  });
};
